import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ClipLoader from 'react-spinners/ClipLoader'
import { useNavigate, useParams } from 'react-router-dom'
import { UserMinus, UserPlus, Trash2, Edit2 } from 'react-feather'
import PrivateLayout from '../components/PrivateLayout'
import Footer from '../components/Footer'
import OrganizationSummary from '../components/OrganizationSummary'
import {
  useAddGrantUserMutation,
  useDeleteGrantUserMutation,
  useDeleteWorkMutation,
  useLazyGetGrantQuery,
  useLazyGetGrantUsersQuery,
  useLazyGetGrantWorksQuery,
  useUpdateGrantUserMutation,
} from '../store/api'
import { FilterOption, GrantData, GrantUserData, OrganizationWorkData, UserData, WorkData } from '../types'
import { connect } from 'react-redux'
import { formatPrice, convertWorkType } from '../utils'
import moment from 'moment'
import { clearGrant, setGrantWorks, setGrantUsers } from '../store/slices'
import clsx from 'clsx'
import { toast } from 'react-toastify'

const defaultResearcherData: GrantUserData = {
  id: '',
  firstName: '',
  lastName: '',
  email: '',
  role: '',
  grantId: '',
  organizationId: '',
}

interface GrantDetailsStateProps {
  user: UserData
  grant: GrantData | null
  grantUsers: GrantUserData[]
  grantWorks: OrganizationWorkData[]
}

interface GrantDetailsDispatchProps {
  clearGrant: () => void
  setGrantWorks: (payload: OrganizationWorkData[]) => void
  setGrantUsers: (payload: GrantUserData[]) => void
}

type GrantDetailsProps = GrantDetailsStateProps & GrantDetailsDispatchProps

const roleOptions: FilterOption[] = [
  {
    label: 'Select role',
    value: '',
  },
  {
    label: 'Primary Contact',
    value: 'Primary Contact',
  },
  {
    label: 'Project Leader',
    value: 'Project Leader',
  },
  {
    label: 'Project Member',
    value: 'Project Member',
  },
  {
    label: 'Additional',
    value: 'Additional',
  },
]

const GrantDetails = ({
  user,
  grant,
  grantWorks,
  grantUsers,
  clearGrant,
  setGrantWorks,
  setGrantUsers,
}: GrantDetailsProps): JSX.Element => {
  const navigate = useNavigate()

  const { grantId } = useParams()
  const [getGrant] = useLazyGetGrantQuery()
  const [getGrantUsers] = useLazyGetGrantUsersQuery()
  const [getGrantWorks] = useLazyGetGrantWorksQuery()
  const [deleteGrantUser] = useDeleteGrantUserMutation()
  const [addGrantUser] = useAddGrantUserMutation()
  const [updateGrantUser] = useUpdateGrantUserMutation()
  const [deleteWork] = useDeleteWorkMutation()

  const [activeTab, setActiveTab] = useState<'works' | 'researchers'>('works')
  const [searchValue, setSearchValue] = useState<string>('')
  const [selectedWork, setSelectedWork] = useState<OrganizationWorkData | null>(null)
  const [deletedGrantUserId, setDeletedGrantUserId] = useState<string>('')
  const [touchedGrantUserIds, setTouchedGrantUserIds] = useState<string[]>([])
  const [isGetGrantApiRequestInProgress, setIsGetGrantApiRequestInProgress] = useState<boolean>(false)
  const [isGetGrantWorksApiRequestInProgress, setIsGetGrantWorksApiRequestInProgress] = useState<boolean>(false)
  const [isDeleteGrantUserApiRequestInProgress, setIsDeleteGrantUserApiRequestInProgress] = useState<boolean>(false)
  const [isSaveGrantUserUpdatesApiRequestInProgress, setSaveGrantUserUpdatesApiRequestInProgress] =
    useState<boolean>(false)
  const [isGetGrantUsersApiRequestInProgress, setIsGetGrantUsersApiRequestInProgress] = useState<boolean>(false)
  const [isDeleteApiRequestInProgress, setIsDeleteApiRequestInProgress] = useState<boolean>(false)

  const isFunder = useMemo(() => {
    return user && user.roles.includes('ROLE_FUNDER')
  }, [user])

  const filteredWorks: OrganizationWorkData[] = useMemo(() => {
    return grantWorks.filter(
      (item) => item.workProduct && item.workProduct.title.toLowerCase().includes(searchValue.toLowerCase()),
    )
  }, [grantWorks, searchValue])

  const loadData = useCallback(async () => {
    if (grantId) {
      // Get grant details
      try {
        setIsGetGrantApiRequestInProgress(true)

        await getGrant(grantId).unwrap()

        setIsGetGrantApiRequestInProgress(false)
      } catch (e) {
        console.error(e)
        setIsGetGrantApiRequestInProgress(false)
      }

      // Get grant users
      try {
        setIsGetGrantUsersApiRequestInProgress(true)

        await getGrantUsers(grantId).unwrap()

        setIsGetGrantUsersApiRequestInProgress(false)
      } catch (e) {
        console.error(e)
        setIsGetGrantUsersApiRequestInProgress(false)
      }

      // Get grant works
      try {
        setIsGetGrantWorksApiRequestInProgress(true)

        await getGrantWorks(grantId).unwrap()

        setIsGetGrantWorksApiRequestInProgress(false)
      } catch (e) {
        console.error(e)
        setIsGetGrantWorksApiRequestInProgress(false)
      }
    }
  }, [])

  const showPrimaryContactHint = useMemo(() => {
    return !grantUsers.some((item) => item.role === 'Primary Contact')
  }, [grantUsers])

  const showResearcherHint = useMemo(() => {
    return !grantUsers.every((item) => item.firstName && item.lastName && item.email && item.role)
  }, [grantUsers])

  const showPrimaryContactAmountHint = useMemo(() => {
    const primaryContacts = grantUsers.filter((item) => item.role === 'Primary Contact')

    return primaryContacts.length > 1
  }, [grantUsers])

  const addNewResearcher = useCallback(() => {
    const newResearcherList: GrantUserData[] = [
      ...grantUsers,
      {
        ...defaultResearcherData,
      },
    ]

    setGrantUsers(newResearcherList)
  }, [grantUsers])

  useEffect(() => {
    clearGrant()
    setGrantWorks([])
    loadData()
  }, [grantId])

  const addTouchedGrantUserId = useCallback(
    (grantUserId: string) => {
      if (!touchedGrantUserIds.includes(grantUserId)) {
        setTouchedGrantUserIds([...touchedGrantUserIds, grantUserId])
      }
    },
    [touchedGrantUserIds],
  )

  const deleteTouchedGrantUserId = useCallback(
    (grantUserId: string) => {
      setTouchedGrantUserIds(touchedGrantUserIds.filter((item) => item !== grantUserId))
    },
    [touchedGrantUserIds],
  )

  const deleteResearcher = useCallback(
    async (index: number) => {
      const itemToDelete = grantUsers[index]

      if (!itemToDelete) {
        return
      }

      if (itemToDelete.id) {
        setDeletedGrantUserId(itemToDelete.id)
        deleteTouchedGrantUserId(itemToDelete.id)
        setIsDeleteGrantUserApiRequestInProgress(true)

        try {
          await deleteGrantUser({ grantUserId: itemToDelete.id })
          setIsDeleteGrantUserApiRequestInProgress(false)
          toast('Successfully deleted the grant user')
          setDeletedGrantUserId('')
        } catch (e) {
          console.error(e)
          setIsDeleteGrantUserApiRequestInProgress(false)
          setDeletedGrantUserId('')
          toast.error('Failed to delete the grant user')

          return
        }
      }

      const newGrantUserList: GrantUserData[] = grantUsers.filter((item: GrantUserData, i: number) => i !== index)
      setGrantUsers(newGrantUserList)
    },
    [grantUsers, deleteTouchedGrantUserId],
  )

  const isSaveGrantUserUpdatesButtonEnabled = useMemo(() => {
    const newRecords: GrantUserData[] = grantUsers.filter((item) => !item.id)

    if (showPrimaryContactAmountHint || showResearcherHint || showPrimaryContactHint) {
      return false
    }

    return newRecords.length > 0 || touchedGrantUserIds.length > 0
  }, [touchedGrantUserIds, grantUsers])

  const saveGrantUserUpdates = useCallback(async () => {
    if (!grant) {
      return
    }

    setSaveGrantUserUpdatesApiRequestInProgress(true)

    try {
      // Handle new grant users
      const newRecords: GrantUserData[] = grantUsers.filter((item) => !item.id)

      for (let i = 0; i < newRecords.length; i++) {
        const newRecord: GrantUserData = newRecords[i]

        await addGrantUser({
          data: {
            firstName: newRecord.firstName,
            lastName: newRecord.lastName,
            email: newRecord.email,
            role: newRecord.role,
          },
          grantId: grant.id,
        }).unwrap()
      }

      // Handle old grant users
      if (touchedGrantUserIds.length) {
        const oldRecords: GrantUserData[] = grantUsers.filter(
          (item) => item.id && touchedGrantUserIds.includes(item.id),
        )

        for (let i = 0; i < oldRecords.length; i++) {
          const oldRecord: GrantUserData = oldRecords[i]
          await updateGrantUser({ data: oldRecord, grantUserId: oldRecord.id }).unwrap()
        }
      }

      toast('Successfully saved the grant user updates')
      setSaveGrantUserUpdatesApiRequestInProgress(false)
      setTouchedGrantUserIds([])

      if (grantId) {
        getGrantUsers(grantId)
      }
    } catch (e) {
      console.error(e)
      setSaveGrantUserUpdatesApiRequestInProgress(false)
      toast.error('Failed to save the grant user updates')
    }
  }, [grantUsers, grant])

  const onDeleteWorkClicked = (work: OrganizationWorkData): void => {
    setSelectedWork(work)
  }

  const handleDeleteWorkProduct = async (): Promise<void> => {
    if (selectedWork) {
      try {
        setIsDeleteApiRequestInProgress(true)
        await deleteWork(selectedWork.id).unwrap()
        setSelectedWork(null)

        if (grantId) {
          // Get grant works
          try {
            setIsGetGrantWorksApiRequestInProgress(true)

            await getGrantWorks(grantId).unwrap()

            setIsGetGrantWorksApiRequestInProgress(false)
          } catch (e) {
            console.error(e)
            setIsGetGrantWorksApiRequestInProgress(false)
          }
        }

        const modals = document.querySelectorAll('#deleteWorkModal')

        for (let i = 0; i < modals.length; i++) {
          modals[i]?.setAttribute('style', 'display: none')
        }

        document.querySelector('.modal-backdrop')?.remove()
        document.querySelector('body')?.classList.remove('modal-open')

        toast('Successfully deleted the work product')
      } catch (error) {
        console.error('Failed to delete the work product:', error)
        toast.error('Failed to delete the work product')
      } finally {
        setIsDeleteApiRequestInProgress(false)
      }
    }
  }

  return (
    <PrivateLayout>
      <div className="content">
        <OrganizationSummary />

        <div className="row mt-3">
          <div className="d-flex align-items-center">
            <h3 className="mb-0 me-3">Grant Details</h3>
            {isGetGrantApiRequestInProgress ? (
              <div className="spinner-wrapper ms-3" style={{ width: 'unset' }}>
                <ClipLoader color="#e5780b" loading={isGetGrantApiRequestInProgress} size={20} />
              </div>
            ) : null}
          </div>
          {isGetGrantApiRequestInProgress ? null : (
            <div className="d-flex align-items-end">
              <table className="table table-borderless mt-4">
                <tbody>
                  <tr>
                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">Grant Number</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <h5 className="lh-sm fw-normal text-800">{grant?.externalId}</h5>
                    </td>

                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">Project Title</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <h5 className="lh-sm fw-normal text-800">{grant?.projectTitle}</h5>
                    </td>
                  </tr>

                  <tr>
                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">Approved Date</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <h5 className="lh-lg fw-normal text-800">
                        {grant && grant.startDate ? moment(grant.startDate).format('MMM DD, YYYY') : ''}
                      </h5>
                    </td>
                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">End Date</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <h5 className="lh-sm fw-normal text-800">
                        {grant && grant.endDate ? moment(grant.endDate).format('MMM DD, YYYY') : ''}
                      </h5>
                    </td>
                  </tr>

                  <tr>
                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">Granted Amount</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <h5 className="lh-lg fw-normal text-800">
                        {grant && grant.totalAmount ? `$${formatPrice(grant.totalAmount)}` : ''}
                      </h5>
                    </td>

                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">Agreement Amount</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <h5 className="lh-sm fw-normal text-800">
                        {grant && grant.agreementAmount ? `$${formatPrice(grant.agreementAmount)}` : ''}
                      </h5>
                    </td>
                  </tr>

                  <tr>
                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">Primary Contact</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <h5 className="lh-sm fw-normal text-800">{(grant && grant.primaryContactName) ?? ''}</h5>
                    </td>
                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">Email</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <h5 className="lh-sm fw-normal text-800">{(grant && grant.primaryContactEmail) ?? ''}</h5>
                    </td>
                  </tr>

                  <tr>
                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">Status</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <h5 className="lh-sm fw-normal text-800">
                        {grant && grant.requestStatus ? grant.requestStatus : ''}
                      </h5>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="row mt-3 mb-3 nav-tabs-wrapper">
          <div className="col-12">
            <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white pt-6 border-top border-300">
              <div className="nav-tabs-wrapper row bg-white mb-4">
                <div className="nopadding">
                  <ul className="nav nav-tabs px-5">
                    <li className="nav-item px-3">
                      <a
                        className={clsx('nav-link', { active: activeTab === 'works' })}
                        onClick={() => {
                          setActiveTab('works')
                        }}
                      >
                        Work Products
                      </a>
                    </li>
                    <li className="nav-item px-3">
                      <a
                        className={clsx('nav-link', { active: activeTab === 'researchers' })}
                        onClick={() => {
                          setActiveTab('researchers')
                        }}
                      >
                        Researchers
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              {activeTab === 'works' ? (
                <div className="ps-2" id="projectSummary">
                  <div className="row align-items-end justify-content-between pb-4 g-3">
                    <div className="col-auto d-flex align-items-center">
                      <p className="text-700 lh-sm mb-0">All work products attached to this grant</p>
                      {isGetGrantWorksApiRequestInProgress || isGetGrantApiRequestInProgress ? (
                        <div className="spinner-wrapper ms-3" style={{ width: 'unset' }}>
                          <ClipLoader
                            color="#e5780b"
                            loading={isGetGrantWorksApiRequestInProgress || isGetGrantApiRequestInProgress}
                            size={20}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="row align-items-center g-3 justify-content-between justify-content-start mb-3">
                    <div className="col col-auto">
                      <div className="search-box mb-2 mb-sm-0">
                        <form className="position-relative" data-bs-toggle="search" data-bs-display="static">
                          <input
                            className="form-control search-input search"
                            type="search"
                            placeholder="Search Work"
                            aria-label="Search"
                            value={searchValue}
                            onChange={(e) => {
                              setSearchValue(e.target.value)
                            }}
                          />
                          <span className="fas fa-search search-box-icon"></span>
                        </form>
                      </div>
                    </div>
                    <div className="col-auto">
                      <div className="d-flex align-items-center">
                        <div className="btn-group me-1">
                          <button
                            className="btn dropdown-toggle mb-1 btn-primary"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                          >
                            <span className="fas fa-plus me-2"></span>Add Work Product
                          </button>
                          <div className="dropdown-menu">
                            <button
                              className="dropdown-item"
                              onClick={() => {
                                navigate(`/grants/${grantId}/works/add?type=search`)
                              }}
                            >
                              Crossref Search
                            </button>
                            <button
                              className="dropdown-item"
                              onClick={() => {
                                navigate(`/grants/${grantId}/works/add?type=manual`)
                              }}
                            >
                              Manually
                            </button>
                            {/*
                          <a className="dropdown-item" href="./work-add-podcast.html">
                            Podcast
                          </a>
                          <a className="dropdown-item" href="./work-add-podcast-episode.html">
                            Podcast Episode
                          </a>
                          */}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="table-responsive ms-n1 ps-1 scrollbar">
                    {filteredWorks.length ? (
                      <table className="table fs--1 mb-0 border-top border-200">
                        <thead>
                          <tr>
                            <th className="sort align-middle ps-3" scope="col" style={{ width: '10%' }}>
                              Type
                            </th>
                            <th className="sort align-middle ps-3" scope="col" style={{ width: '10%' }}>
                              DOI
                            </th>
                            <th
                              className="sort white-space-nowrap align-middle ps-3"
                              scope="col"
                              style={{ width: '30%' }}
                            >
                              Title
                            </th>
                            <th className="sort align-middle ps-3" scope="col" style={{ width: '10%' }}>
                              Publisher
                            </th>
                            <th className="sort align-middle ps-3" scope="col" style={{ width: '12%' }}>
                              Published
                            </th>
                            <th
                              className="sort align-middle ps-3"
                              scope="col"
                              data-sort="type"
                              style={{ width: '10%' }}
                            >
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className="list" id="project-summary-table-body">
                          {filteredWorks.map((organizationWork) => (
                            <tr key={organizationWork.workProduct.id} className="position-static">
                              <td className="align-middle white-space-nowrap ps-3 type">
                                <p className="mb-0 fs--1 text-900">
                                  {convertWorkType(organizationWork.workProduct.workProductType)}
                                </p>
                              </td>
                              <td className="align-middle time white-space-nowrap ps-3 doi">
                                {organizationWork.workProduct.doi ? (
                                  <a
                                    className="fw-normal fs--1"
                                    href={`http://dx.doi.org/${organizationWork.workProduct.doi}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {organizationWork.workProduct.doi}
                                  </a>
                                ) : null}
                              </td>
                              <td
                                className="align-middle white-space-nowrap ps-3 title"
                                style={{ maxWidth: '400px', whiteSpace: 'normal', textAlign: 'left' }}
                              >
                                <button
                                  className="btn-link mb-0 fs--1 text-start"
                                  style={{ color: '#3874ff' }}
                                  onClick={() => {
                                    navigate(`/works/${organizationWork.id}/details`)
                                  }}
                                >
                                  {organizationWork.workProduct.title}
                                </button>
                              </td>
                              <td className="align-middle white-space-nowrap ps-3 publisher">
                                <p className="mb-0 fs--1 text-900">{organizationWork.workProduct.publisher}</p>
                              </td>
                              <td className="align-middle white-space-nowrap ps-3 published">
                                <p className="mb-0 fs--1 text-900">
                                  {organizationWork.workProduct.publicationDate
                                    ? moment(organizationWork.workProduct.publicationDate).format('MMM, YYYY')
                                    : ''}
                                </p>
                              </td>
                              <td className="align-middle white-space-nowrap ps-3 type">
                                <div className="d-flex justify-content-start sort align-middle ps-3">
                                  <Edit2
                                    className="me-3 text-success-300 pointer"
                                    size={18}
                                    onClick={() => navigate(`/works/${organizationWork.id}/edit`)}
                                  />
                                  <Trash2
                                    className="text-danger-300 pointer"
                                    data-bs-toggle="modal"
                                    data-bs-target="#deleteWorkModal"
                                    size={18}
                                    onClick={() => onDeleteWorkClicked(organizationWork)}
                                  />
                                  <div
                                    className="modal fade"
                                    id="deleteWorkModal"
                                    tabIndex={-1}
                                    aria-labelledby="verticallyCenteredModalLabel"
                                    style={{ display: 'none' }}
                                    aria-hidden="true"
                                  >
                                    <div className="modal-dialog modal-dialog-centered">
                                      <div className="modal-content">
                                        <div className="modal-header">
                                          <h5
                                            className="modal-title"
                                            id={`verticallyCenteredModalLabel`}
                                            style={{ whiteSpace: 'normal' }}
                                          >
                                            Do you really want to delete this work product?
                                          </h5>
                                        </div>
                                        <div className="modal-footer">
                                          <button
                                            className="btn btn-primary"
                                            onClick={handleDeleteWorkProduct}
                                            type="button"
                                            style={{ width: '90px' }}
                                          >
                                            {isDeleteApiRequestInProgress ? (
                                              <ClipLoader
                                                color="white"
                                                loading={isDeleteApiRequestInProgress}
                                                size={15}
                                              />
                                            ) : (
                                              'Delete'
                                            )}
                                          </button>
                                          <button
                                            className="btn btn-outline-primary"
                                            type="button"
                                            data-bs-dismiss="modal"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="my-7 text-center">No Work Product found</div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="row align-items-center justify-content-between pb-4 g-3">
                    <div className="col-auto d-flex align-items-center">
                      <p className="text-700 lh-sm mb-0">All researchers added for this grant</p>
                      {isGetGrantUsersApiRequestInProgress || isGetGrantApiRequestInProgress ? (
                        <div className="spinner-wrapper ms-3" style={{ width: 'unset' }}>
                          <ClipLoader
                            color="#e5780b"
                            loading={isGetGrantUsersApiRequestInProgress || isGetGrantApiRequestInProgress}
                            size={20}
                          />
                        </div>
                      ) : null}
                    </div>
                    {isFunder ? (
                      <div className="col-auto bg-white">
                        <button
                          type="button"
                          className="btn btn-primary d-flex"
                          style={{ color: 'white', width: '200px' }}
                          onClick={() => {
                            addNewResearcher()
                          }}
                        >
                          <UserPlus size={16} />
                          <span className="ms-3">Add Grant User</span>
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <div className="row col-12 bg-white pb-6 gy-3">
                    {grantUsers.map((researcher: GrantUserData, i: number) => (
                      <>
                        <div className="col-sm-6 col-md-4">
                          <div className="form-floating">
                            <input
                              className="form-control"
                              id="firstName"
                              type="text"
                              placeholder="First Name"
                              value={researcher.firstName}
                              onChange={(e) => {
                                setGrantUsers(
                                  grantUsers.map((item: GrantUserData, index: number) =>
                                    index === i
                                      ? {
                                          ...item,
                                          firstName: e.target.value,
                                        }
                                      : item,
                                  ),
                                )

                                if (researcher.id) {
                                  addTouchedGrantUserId(researcher.id)
                                }
                              }}
                            />
                            <label htmlFor="firstName">First Name</label>
                          </div>
                        </div>
                        <div className="col-sm-6 col-md-4">
                          <div className="form-floating">
                            <input
                              className="form-control"
                              id="lastName"
                              type="text"
                              placeholder="Last Name"
                              value={researcher.lastName}
                              onChange={(e) => {
                                setGrantUsers(
                                  grantUsers.map((item: GrantUserData, index: number) =>
                                    index === i
                                      ? {
                                          ...item,
                                          lastName: e.target.value,
                                        }
                                      : item,
                                  ),
                                )

                                if (researcher.id) {
                                  addTouchedGrantUserId(researcher.id)
                                }
                              }}
                            />
                            <label htmlFor="lastName">Last Name</label>
                          </div>
                        </div>
                        <div className="col-sm-6 col-md-4">
                          <div className="form-floating">
                            <input
                              className="form-control"
                              id="email"
                              type="email"
                              placeholder="Email"
                              value={researcher.email}
                              readOnly={!!researcher.id}
                              onChange={(e) => {
                                setGrantUsers(
                                  grantUsers.map((item: GrantUserData, index: number) =>
                                    index === i
                                      ? {
                                          ...item,
                                          email: e.target.value,
                                        }
                                      : item,
                                  ),
                                )

                                if (researcher.id) {
                                  addTouchedGrantUserId(researcher.id)
                                }
                              }}
                            />
                            <label htmlFor="email">Email</label>
                          </div>
                        </div>
                        <div className="col-sm-6 col-md-4">
                          <div className="form-floating">
                            <select
                              className="form-select"
                              id="role"
                              value={researcher.role}
                              onChange={(e) => {
                                setGrantUsers(
                                  grantUsers.map((item: GrantUserData, index: number) =>
                                    index === i
                                      ? {
                                          ...item,
                                          role: e.target.value,
                                        }
                                      : item,
                                  ),
                                )

                                if (researcher.id) {
                                  addTouchedGrantUserId(researcher.id)
                                }
                              }}
                            >
                              {roleOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <label htmlFor="role">Role</label>
                          </div>
                        </div>

                        <div className="col-sm-6 col-md-4">
                          {isFunder && grantUsers.length > 1 ? (
                            <button
                              className="btn-link fw-bold fs--1 mt-4 me-3"
                              onClick={() => {
                                deleteResearcher(i)
                              }}
                            >
                              <UserMinus className="text-danger-300" size={16} />
                            </button>
                          ) : null}
                          {deletedGrantUserId === researcher.id && isDeleteGrantUserApiRequestInProgress ? (
                            <ClipLoader
                              color="#e5780b"
                              loading={isDeleteGrantUserApiRequestInProgress}
                              size={20}
                              className="ms-3"
                            />
                          ) : null}
                        </div>

                        <hr className="bg-200 mb-4 mt-4" />
                      </>
                    ))}
                    {showPrimaryContactHint ? (
                      <div className="hint error mt-3">At least one primary contact is required for the grant</div>
                    ) : null}
                    {showResearcherHint ? (
                      <div className="hint error mt-3">At least 1 researcher is not fully filled</div>
                    ) : null}
                    {showPrimaryContactAmountHint ? (
                      <div className="hint error mt-3">Maximum 1 primary contact can be added for the grant</div>
                    ) : null}
                  </div>
                  <div className="col-12 gy-6">
                    <div className="row g-3 justify-content-end pb-3">
                      <div className="col-auto">
                        <button
                          type="button"
                          className="btn btn-primary px-3 px-sm-3"
                          style={{ width: '200px' }}
                          disabled={!isSaveGrantUserUpdatesButtonEnabled}
                          onClick={() => {
                            saveGrantUserUpdates()
                          }}
                        >
                          {isSaveGrantUserUpdatesApiRequestInProgress ? (
                            <ClipLoader color="white" loading={isSaveGrantUserUpdatesApiRequestInProgress} size={15} />
                          ) : (
                            'Save updates'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </PrivateLayout>
  )
}

const mapStateToProps = (state: any): GrantDetailsStateProps => {
  return {
    user: state.auth.user,
    grant: state.grant.grant,
    grantUsers: state.grant.grantUsers,
    grantWorks: state.grant.grantWorks,
  }
}

const mapDispatchToProps = (dispatch: any): GrantDetailsDispatchProps => {
  return {
    clearGrant: () => dispatch(clearGrant()),
    setGrantWorks: (payload: OrganizationWorkData[]) => dispatch(setGrantWorks(payload)),
    setGrantUsers: (payload: GrantUserData[]) => dispatch(setGrantUsers(payload)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(GrantDetails)
